// Settings: appearance, the daily notification, schedules, and the way out.
//
// THIS SCREEN EXISTS BECAUSE A SCHEDULE HAD NOWHERE TO LIVE. A schedule was only
// ever openable from its card on the day, and a day leaves out the schedules
// with nothing due in it - so a weekend-only or monthly schedule was uneditable
// on most days of the week, and an archived one was reachable for exactly as
// long as an Undo stayed on screen. Both are the same hole: the day is not a
// list of your schedules and was being used as one. This is that list.
//
// It also carries SIGN OUT, which had no home in the client at all. The server
// side was built and correct the whole time (`POST /api/v1/auth/sign-out`);
// nothing called it, so the only way out of a session was to clear cookies by
// hand. The captain hit that on the live site.
//
// Settings is not a tab (`screens.ts`): it is reached from the account button in
// each screen's top row on a phone and from the foot of the sidebar on a
// desktop, which is the approved design and what keeps a preference off the
// daily path. It holds APPEARANCE, FORMATTING, THE DAILY NOTIFICATION, THE
// SCHEDULES, EXPORT and SIGN OUT, and nothing else - the Board row that shipped
// here in v0.4.0 is a tab of its own now (captain, 2026-08-18), and a screen this
// one can already reach is not a row here. Formatting is a REFERENCE and not a
// preference: it is here because this is where depth one level down already
// lives, and because the alternative was a resting toolbar on the notes screen.
//
// APPEARANCE IS FIRST, which is the prototype's own order and not an accident:
// it is the group a stranger opens Settings to find, and the schedules below it
// are a list that grows, so anything under that list drifts off the first
// screenful as an account fills up.

import { Fragment, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { api, isSignedOut, messageFor } from '../api/client.ts';
import type { Section } from '../api/client.ts';
import { capNotice } from '../day/day-model.ts';
import { Icon } from '../day/Icon.tsx';
import { hueIn, PALETTE, scheduleList } from '../edit/edit-model.ts';
import { Notice } from '../edit/Sheet.tsx';
import { FORMATTING, modifierLabel } from '../notes/note-formatting.ts';
import { NotificationSetting } from '../notify/NotificationSetting.tsx';
import {
  MODES,
  THEMES,
  applyAppearance,
  readAppearance,
  storeAppearance,
  useMode,
} from './theme.ts';
import type { Appearance } from './theme.ts';
import { VOICES, storeVoice, useVoice } from './voice.ts';

interface Props {
  /**
   * Bumped by every write the editor makes. This screen holds a read of its own
   * - the account's schedules, which no day payload carries - so archiving from
   * the sheet it opens has to reach it, and this is how.
   */
  revision: number;
  /**
   * The account's active schedules and the cap, both from the day payload, or
   * null while no day has arrived. NOT counted from the list below: the figures
   * are the database's, and `cap` is `section_cap()` travelling rather than any
   * client holding a copy of the number (AGENTS.md).
   */
  schedules: { total: number; cap: number } | null;
  onBack: () => void;
  onEditSchedule: (section: Section) => void;
  /** Resolves when the write has been made, so the row can wait for its own. */
  onRestore: (section: Section) => Promise<void>;
  /** Any 401, from this screen's own read or from signing out. */
  onSignedOut: () => void;
  /** The transient line at the foot of the screen. */
  say: (message: string) => void;
}

/**
 * The account's schedules, re-read whenever something was written.
 *
 * It keeps whatever it last had while the next read is in flight, deliberately:
 * emptying it would flash "Loading…" over the whole list every time a schedule
 * is renamed, which is a flash of nothing in exchange for no information. The
 * day screen's own cache makes the same trade for the same reason
 * (`useDayScreen.refresh`).
 */
function useSchedules(revision: number, onSignedOut: () => void) {
  const [sections, setSections] = useState<Section[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  // The effect depends on WHICH read is wanted, not on how the caller is
  // rendered: `onSignedOut` is a fresh closure every render and depending on it
  // would re-read the account on every keystroke anywhere in the shell.
  const latest = useRef(onSignedOut);
  latest.current = onSignedOut;

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const value = await api.sections({ includeArchived: true });
        if (!live) return;
        setSections(value);
        setProblem(null);
      } catch (error) {
        if (isSignedOut(error)) {
          latest.current();
          return;
        }
        if (live) setProblem(messageFor(error));
      }
    })();
    return () => {
      live = false;
    };
  }, [revision]);

  return { sections, problem };
}

/**
 * The chosen look, and the document kept in step with it.
 *
 * NO SERVER, AND NO STATE ABOVE THIS SCREEN. A theme is two attributes on
 * <html>, so choosing one is a write to the document rather than a value that
 * has to be threaded down from the app - which is what lets the switcher be
 * built without touching `App.tsx` at all.
 */
function useAppearance() {
  const [appearance, setAppearance] = useState<Appearance>(readAppearance);
  // The RESOLVED mode, read from the document rather than held beside it. The
  // previews are drawn in it, and with `System` chosen there is no other way to
  // know which of the eight looks each card should show. `useMode` also means
  // the device changing its mind mid-screen redraws them: `startAppearance` in
  // main.tsx moves the attribute and every reader is told at once.
  const mode = useMode();

  const choose = (next: Appearance) => {
    setAppearance(next);
    // Stored BEFORE it is applied, so a reload during the paint cannot land on a
    // page wearing a look nothing remembers.
    storeAppearance(next);
    applyAppearance(next);
  };

  return { appearance, mode, choose };
}

export function SettingsScreen({
  revision,
  schedules,
  onBack,
  onEditSchedule,
  onRestore,
  onSignedOut,
  say,
}: Props) {
  const { sections, problem } = useSchedules(revision, onSignedOut);
  const [leaving, setLeaving] = useState(false);
  // Which rows have a restore in flight. Its own button waits for it, the way
  // every button in a sheet waits for `busy` - a second press sends a second
  // PATCH and a second line saying the same thing.
  //
  // A marker PER ROW rather than one for the screen. There can be several
  // archived schedules, so a single marker belongs to whichever row moved last:
  // starting a second restore released the first row's button, and the first
  // one finishing released the second row's, each while its own PATCH and the
  // day read behind it were still going. A set is only ever added to and
  // removed from by the row it names.
  const [restoring, setRestoring] = useState<ReadonlySet<string>>(() => new Set());

  const list = sections === null ? null : scheduleList(sections);
  const atCap = schedules === null ? null : capNotice(schedules);

  const signOutNow = () => {
    setLeaving(true);
    void (async () => {
      try {
        await api.signOut();
      } catch (error) {
        // A 401 means the session was already gone, which is exactly the state
        // that was asked for - so it is a success, not a failure to report.
        if (!isSignedOut(error)) {
          setLeaving(false);
          // Said rather than swallowed: somebody who believes they signed out
          // of a shared machine and did not is the whole reason this matters.
          say(`You are still signed in. ${messageFor(error)}`);
          return;
        }
      }
      // Raises the signed-out screen over everything, which is where a person
      // with no session belongs - and it is never lowered, so the next thing
      // they see is the way back in.
      onSignedOut();
    })();
  };

  return (
    <>
      {/* The way out of a screen that is not a tab. On a phone the tab bar
          below cannot answer for it - Settings is not one of the four - and on
          a desktop the sidebar can, which is why this is here at both widths
          rather than at one: the same control, doing the same thing. */}
      <div className="topbar">
        <button className="iconbtn" aria-label="Back" onClick={onBack}>
          <Icon name="chevL" />
        </button>
        {/* The screen's own heading, at the top of its heading order, the way
            every other screen in this client starts with one. A title bar is
            still where the title of a screen belongs. */}
        <h1>Settings</h1>
        {/* The heading is centred by the two controls either side of it, and
            there is only one. The prototype balances it with an empty box of
            the same size rather than letting the title drift left. */}
        <span className="iconbtn" aria-hidden="true" />
      </div>

      <div className="scroll">
        <div className="wrap">
          {/* NO "Screens" GROUP. The Board had a row here while it was not a
              tab; it is one now (captain, 2026-08-18 - `screens.ts`), and a
              second door onto a screen the tab bar already carries is a second
              place to keep right. Month and Reminders are reached from the day
              they are about, which is the same rule from the other end: this
              screen is preferences and the account, not a directory. */}
          <AppearanceGroup />

          {/* Beside the themes and the wording, which is where "depth, one
              level down, off the daily path" already lives - and above the
              schedules for the same reason Appearance is: the list below grows
              with an account, and anything under it drifts off the first
              screenful. */}
          <FormattingGroup />

          {/* ABOVE THE SCHEDULES, for the reason Appearance is above them: the
              list below grows with the account, so anything under it drifts off
              the first screenful. A whole file of its own, mounted in one line
              (`notify/NotificationSetting.tsx`) - this screen is the one every
              workstream has to touch, and a feature that arrives as an import
              and a tag collides with nobody.

              It holds its own read and its own 401, exactly as the schedules
              list below does, and is gated on being on screen the way the board
              and the month are: nobody who never opens Settings pays a request
              for a feature they have not heard of. */}
          <NotificationSetting active onSignedOut={onSignedOut} />

          <div className="setgroup">
            {/* An h2 under the screen's own h1, so the heading order has no gap
                in it - the same rule the placeholders follow. */}
            <h2>Schedules</h2>

            {/* Only at the cap, and only in the payload's own figures. This is
                where somebody sent here by the cap notice arrives, so the
                sentence that sent them is repeated where it can be acted on. */}
            {atCap !== null && <Notice>{atCap}</Notice>}

            {list === null && problem === null && (
              <p className="sheetwait" role="status">
                Loading…
              </p>
            )}

            {list !== null && list.length === 0 && (
              <div className="setlist">
                <div className="setrow">
                  <span className="st">
                    <b>No schedules yet</b>
                    {/* The ACTION, not the control that carries it: the + is a
                        phone's, and at sidebar width the same action is the
                        navigation's "Add something" with no + drawn anywhere.
                        Naming a button that is not on screen is a dead end. */}
                    <span>Add something on your day makes the first one.</span>
                  </span>
                </div>
              </div>
            )}

            {list !== null && list.length > 0 && (
              <div className="setlist">
                {list.map((section) =>
                  section.archivedAt === null ? (
                    <ScheduleRow
                      key={section.id}
                      section={section}
                      onOpen={() => onEditSchedule(section)}
                    />
                  ) : (
                    <ArchivedRow
                      key={section.id}
                      section={section}
                      busy={restoring.has(section.id)}
                      onRestore={() => {
                        setRestoring((ids) => new Set(ids).add(section.id));
                        void onRestore(section).finally(() =>
                          setRestoring((ids) => {
                            const next = new Set(ids);
                            next.delete(section.id);
                            return next;
                          }),
                        );
                      }}
                    />
                  ),
                )}
              </div>
            )}

            {/* The read failed and whatever was last on screen is still there,
                so this says which of the two you are looking at rather than
                replacing a list that may still be right. */}
            {problem !== null && (
              <p className="sheetproblem" role="alert">
                {list === null ? problem : `This list may be out of date. ${problem}`}
              </p>
            )}

            <p className="finetext">
              Every schedule you have, including the ones today has nothing due in. Archiving takes
              one off your day and keeps everything you finished in it; restoring puts it back.
            </p>
          </div>

          <ExportGroup onSignedOut={onSignedOut} say={say} />

          <div className="setgroup">
            <div className="setlist">
              <button className="setrow" disabled={leaving} onClick={signOutNow}>
                <Icon name="out" />
                <span className="st">
                  <b>{leaving ? 'Signing out…' : 'Sign out'}</b>
                  <span>Ends this session on this device.</span>
                </span>
              </button>
            </div>
            <p className="finetext">
              Perfect Note is free, has no AI, and stores no passwords. Signing back in with Google
              brings everything here back exactly as you left it.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * The way out that is not signing out: everything the account holds, as one
 * file the person keeps.
 *
 * ONE ROW, NOT THE PROTOTYPE'S TWO. `prototype-v1.md` §5 row 8 drew "Export
 * notes as Markdown" and "Export history as CSV" side by side, both tagged
 * `PROTOTYPE`; the captain settled it as one action and one file (2026-08-19),
 * so the zip holds both and there is one thing to press. The group's heading and
 * the sentence under it are the prototype's own words, which were approved.
 *
 * WHY THIS IS IN SETTINGS AND NOWHERE ELSE: it is exactly where the market
 * research put it - "(A) No, it lives in Settings. (B) No, and that is fine;
 * nobody needs to discover it until they need it"
 * (`market-and-competitors.md` §7 item 8). It is off the daily path on purpose.
 *
 * IT KEEPS ITS OWN STATE AND TOUCHES NOTHING ABOVE IT, like `AppearanceGroup`:
 * the screen already has `say` and `onSignedOut`, so this needed no change to
 * `App.tsx` at all.
 */
function ExportGroup({
  onSignedOut,
  say,
}: {
  onSignedOut: () => void;
  say: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  const exportNow = () => {
    setBusy(true);
    void (async () => {
      try {
        const archive = await api.exportArchive();
        keep(archive.filename, archive.blob);
        // Said out loud rather than left to the browser's download shelf, which
        // is a different corner of a different chrome on every device and is
        // hidden entirely on some phones.
        say(`Saved ${archive.filename}.`);
      } catch (error) {
        // A 401 is the session, not the export - it raises the signed-out
        // screen, exactly as it does everywhere else in this client.
        if (isSignedOut(error)) {
          onSignedOut();
          return;
        }
        say(`Could not export. ${messageFor(error)}`);
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <div className="setgroup">
      <h2>Your data</h2>

      <div className="setlist">
        <button className="setrow" disabled={busy} onClick={exportNow}>
          <Icon name="down" />
          <span className="st">
            <b>{busy ? 'Getting your export ready…' : 'Export everything'}</b>
            {/* What is in the file, in the two words that mean something to
                somebody deciding whether to press it. */}
            <span>Your notes as Markdown and your history as CSV, in one zip.</span>
          </span>
        </button>
      </div>

      {/* The prototype's own sentence, which says why this exists rather than
          what it does - and it is PLAIN in both voices, like everything on this
          screen that carries a fact. */}
      <p className="finetext">
        Perfect Note is free and always will be. Nothing here is locked, and your data leaves in
        plain files whenever you want it. The file opens without Perfect Note, and what is not in it
        is listed inside it.
      </p>
    </div>
  );
}

/**
 * Hand the file to the browser.
 *
 * A LINK IT CLICKS ITSELF, because there is no other way: a `download`
 * attribute is what names a saved file, and only an anchor carries one. The
 * archive arrives as a Blob rather than as a navigation so that a 401 raises the
 * signed-out screen instead of drawing a page of JSON (`api/client.ts`).
 *
 * THE LINK IS IN THE DOCUMENT WHILE IT IS CLICKED, which is the one part of this
 * that looks like ceremony and is not. Firefox has historically ignored a
 * programmatic click on a `download` anchor that was never inserted, and a
 * no-op here is the worst outcome this feature has: the person is told their
 * export was saved and no file arrives. It is taken out again on the same turn.
 *
 * The object URL is released on the NEXT turn of the loop and not on this one:
 * the browser starts reading it while the click is being dispatched, and
 * revoking inside that same turn cancels a download that had already begun.
 */
function keep(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * The three hues a preview card shows, which are the three a new account has.
 *
 * Taken from `PALETTE` rather than written out, so the dots in the picker are
 * the colours the schedules actually are - Work blue, School amber, Gym green
 * from the first-run seed - and so a change to the palette cannot leave this
 * showing a colour the product no longer has.
 */
const PREVIEW_HUES = PALETTE.slice(0, 3);

/**
 * Four themes and three modes, and nothing else in the client knows a colour.
 *
 * THE CARDS ARE NOT SWATCHES. Each preview carries `data-theme` and `data-mode`
 * on itself, so the same block of `styles.css` that paints the app paints the
 * card - "what you see in the card is what the app becomes" (`prototype-v1.md`
 * §4). A picker built from copied hexes is a second place every colour has to be
 * kept right, and the one that is never noticed when it drifts.
 *
 * `aria-pressed` rather than radios, which is this client's settled way of
 * drawing a chooser (`edit/Sheet.tsx`): the pressed state is drawn, and a native
 * radio brings a control that cannot be styled into one.
 */
function AppearanceGroup() {
  const { appearance, mode, choose } = useAppearance();
  const voice = useVoice();

  return (
    <div className="setgroup">
      <h2>Appearance</h2>

      <div className="themes" role="group" aria-label="Theme">
        {THEMES.map((theme) => (
          <button
            key={theme.name}
            className="themecard"
            type="button"
            aria-pressed={appearance.theme === theme.name}
            onClick={() => choose({ ...appearance, theme: theme.name })}
          >
            {/* The preview is drawn in the mode the app is IN, not in the
                theme's light values - picking Slate while in dark should show
                what Slate is about to look like, which is Slate dark. */}
            <span className="tprev" data-theme={theme.name} data-mode={mode}>
              <span className="tp1" />
              <span className="tp2" />
              <span className="tpc">
                {PREVIEW_HUES.map((hue) => (
                  <i key={hue.hex} style={{ '--c': hueIn(hue.hex, mode) } as CSSProperties} />
                ))}
              </span>
            </span>
            {/* The name and what the theme is for. Four unlabelled swatches
                say which is chosen and never why anyone would choose it. */}
            <span className="tname">
              {theme.label}
              <em>{theme.note}</em>
            </span>
          </button>
        ))}
      </div>

      <div className="seg segmode" role="group" aria-label="Light or dark">
        {MODES.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={appearance.mode === option.value}
            onClick={() => choose({ ...appearance, mode: option.value })}
          >
            {option.label}
          </button>
        ))}
      </div>

      <p className="finetext">
        System follows your device, which switches on its own at sunset if you have it set to. Ink
        is the highest-contrast of the four, with heavier rules and thicker checkboxes. Your choice
        is kept on this device, so a phone can be dark while a laptop stays light.
      </p>

      {/* WORDING SITS WITH THE OTHER APPEARANCE CHOICES because that is what it
          is: a look, not a mode (`prototype-v2.md` §2.6). It is one row with one
          control, which is the smallest this feature can be on a screen already
          flagged as the one that grows.

          ITS OWN LABEL, ITS DESCRIPTION AND THE FINE PRINT BELOW ARE PLAIN IN
          BOTH VOICES, deliberately: a setting you cannot read is a trap, and
          this is the one control that turns the other voice off again. */}
      <div className="setlist setvoice">
        <div className="setrow">
          <Icon name="speech" />
          <span className="st">
            <b>Wording</b>
            <span>Plain, or a warmer casual voice.</span>
          </span>
          <div className="seg segmini" role="group" aria-label="Wording">
            {VOICES.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={voice === option.value}
                onClick={() => storeVoice(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <p className="finetext">
        Casual changes greetings, empty screens and the odd bit of encouragement. Buttons, labels,
        dates and every number stay exactly the same in both, so nothing gets harder to use.
      </p>
    </div>
  );
}

/**
 * What a note understands, written down once where somebody can go and look.
 *
 * NO TOOLBAR ANYWHERE, and this card is what pays for that. Formatting in a note
 * is typed - `#`, `-`, `**` - which is invisible until somebody is told, so the
 * choice is between a resting toolbar on the daily path and a reference one
 * level down. Obsidian ships no toolbar on the desktop for the same reason, and
 * this product's own rule about where depth belongs already said which.
 *
 * A `<dl>` because that is what it is: a term and what it means. The rows are
 * `note-formatting.ts`'s, next to the parser they describe.
 */
function FormattingGroup() {
  return (
    <div className="setgroup">
      <h2>Formatting</h2>

      <dl className="fmtlist">
        {FORMATTING.map((row) => (
          <Fragment key={row.syntax}>
            <dt>{row.syntax}</dt>
            <dd>
              {row.means}
              {row.key !== undefined && <kbd>{`${modifierLabel()}${row.key}`}</kbd>}
            </dd>
          </Fragment>
        ))}
      </dl>

      {/* THE SEARCH SENTENCE IS HERE ON PURPOSE, and it is the accepted
          behaviour rather than a defect waiting to be fixed. Search matches what
          you typed, markers included - which is what Obsidian's own search does
          - so `**Deadline** is Friday` is not found by "deadline is". Stripping
          the syntax before matching would put a second copy of this vocabulary
          in the API, where nothing else knows what `**` means. Somebody who has
          just failed to find a note needs to know why; `api/src/db/repositories/
          notes.ts` and `web/src/README.md` carry it for whoever reads the code
          instead. */}
      <p className="finetext">
        Notes are stored exactly as you type them, which is what keeps the plain-file export a copy
        rather than a conversion. <b>Write</b> and <b>Read</b> are two views of one note -{' '}
        {modifierLabel()}E switches, and looking changes nothing. Search matches what you typed, so
        formatting marks around a word can break a search that reads across it.
      </p>
    </div>
  );
}

/** A schedule the day may or may not be drawing, opened in the ordinary sheet. */
function ScheduleRow({ section, onOpen }: { section: Section; onOpen: () => void }) {
  const mode = useMode();
  return (
    <button
      className="setrow"
      style={{ '--c': hueIn(section.colorHex, mode) } as CSSProperties}
      onClick={onOpen}
    >
      <span className="chip">
        <Icon name={section.symbol} />
      </span>
      <span className="st">
        <b>{section.name}</b>
      </span>
      <span className="chev">
        <Icon name="chevR" />
      </span>
    </button>
  );
}

/**
 * An archived schedule, and the button that ends the only state in this product
 * a person could previously not get out of.
 *
 * Not a `<button className="setrow">` with the Restore inside it: a row that is
 * itself a button cannot hold another one, and there is nothing else to open -
 * an archived schedule is not edited, it is put back first.
 */
function ArchivedRow({
  section,
  busy,
  onRestore,
}: {
  section: Section;
  busy: boolean;
  onRestore: () => void;
}) {
  const mode = useMode();
  return (
    <div className="setrow" style={{ '--c': hueIn(section.colorHex, mode) } as CSSProperties}>
      <span className="chip">
        <Icon name={section.symbol} />
      </span>
      <span className="st">
        <b>{section.name}</b>
        {/* The prototype's own words for this row, and short enough to stay on
            one line beside the button on a phone. What "history kept" means is
            spelled out once under the list rather than in every row. */}
        <span>Archived - history kept</span>
      </span>
      {/* Named for the schedule it acts on. Several archived schedules make
          several buttons whose visible word is the same one, and a list of
          controls that reads "Restore, Restore, Restore" says which button
          exists and not which schedule it puts back. The word stays on screen
          short - the row beside it is what a sighted reader has. */}
      <button
        className="btn"
        aria-label={`${busy ? 'Restoring' : 'Restore'} ${section.name}`}
        disabled={busy}
        onClick={onRestore}
      >
        {busy ? 'Restoring…' : 'Restore'}
      </button>
    </div>
  );
}
